import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';
import { TOURNAMENT_FALLBACK } from '../data';
import Btn from '../ui/Btn';
import OAuthButtons from '../ui/OAuthButtons';
import Spinner from '../ui/Spinner';
import { Field, Input } from '../ui/Field';
import PhoneInput, { isValidKzPhone, rawPhone } from '../ui/PhoneInput';
import Crosshair from '../brand/Crosshair';
import Logo from '../brand/Logo';

export default function AuthPage({ initialMode = 'login' }) {
  const { loginUser, registerUser, fetchActiveTournament } = useAppStore();
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode);
  const [active, setActive] = useState(null);
  useEffect(() => { fetchActiveTournament().then(setActive); }, [fetchActiveTournament]);
  const stats = {
    registered: active?.teams_count ?? TOURNAMENT_FALLBACK.registered,
    players: active?.players_count ?? TOURNAMENT_FALLBACK.players,
  };
  const [f, setF] = useState({ name: '', email: '', pass: '', nick: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState({});
  const set = (k, v) => { setF(s => ({ ...s, [k]: v })); setErr(e => ({ ...e, [k]: null })); };

  const submit = async () => {
    const e = {};
    if (mode === 'signup' && !f.name.trim()) e.name = t('auth.nameRequired') || 'Укажите имя';
    if (mode === 'signup' && !f.nick.trim()) e.nick = t('auth.nickRequired') || 'Укажите ник';
    if (mode === 'signup' && !isValidKzPhone(f.phone)) e.phone = 'Введите номер в формате +7 (7XX) XXX-XX-XX';
    if (mode === 'signup' && !/.+@.+\..+/.test(f.email)) e.email = t('auth.emailInvalid') || 'Некорректный e-mail';
    if (mode === 'signup' && f.pass.length < 8) e.pass = t('auth.passMin') || 'Минимум 8 символов';
    if (mode === 'login' && !f.pass) e.pass = t('auth.passRequired') || 'Введите пароль';
    setErr(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      if (mode === 'login') {
        await loginUser({ identifier: f.email, password: f.pass });
      } else {
        await registerUser({ name: f.name, email: f.email, nickname: f.nick, phone: rawPhone(f.phone), password: f.pass, full_name: f.name });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-in" style={{ position: 'relative', zIndex: 1, minHeight: 'calc(100vh - 72px)', display: 'flex' }}>
      {/* LEFT — brand panel */}
      <div className="auth-aside" style={{ position: 'relative', flex: '0 0 44%', overflow: 'hidden', background: 'linear-gradient(150deg, #1a0405, #0a0a0c 65%)', borderRight: '1px solid var(--line-accent)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '48px 44px' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(/assets/logo-federation.png)', backgroundSize: '120%', backgroundPosition: 'center', opacity: 0.08, filter: 'grayscale(0.2)' }} />
        <div style={{ position: 'absolute', top: '-20%', left: '-10%', width: '70%', height: '60%', background: 'radial-gradient(circle, var(--accent-glow), transparent 65%)', filter: 'blur(30px)' }} />
        <div style={{ position: 'relative' }}><Logo size={48} glow withText /></div>
        <div style={{ position: 'relative' }}>
          <Crosshair style={{ width: 56, marginBottom: 24, opacity: 0.7 }} />
          <h2 className="display" style={{ fontSize: 'clamp(30px, 4vw, 46px)', margin: 0, lineHeight: 0.95 }}>
            {t('auth.tagline')}<br /><span className="grad-red">{t('auth.taglineSub')}</span>
          </h2>
          <p style={{ color: 'var(--fg-soft)', marginTop: 16, maxWidth: 340, lineHeight: 1.55, fontSize: 15 }}>
            Заявки, статус верификации, твоя команда и сетка — всё в твоём личном кабинете.
          </p>
        </div>
        <div className="row gap-4" style={{ position: 'relative' }}>
          <div className="col">
            <div className="display grad-text" style={{ fontSize: 26 }}>{stats.registered}</div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', letterSpacing: '0.15em' }}>{t('auth.teams')}</div>
          </div>
          <div className="col">
            <div className="display grad-text" style={{ fontSize: 26 }}>{stats.players}</div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', letterSpacing: '0.15em' }}>{t('auth.players')}</div>
          </div>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="center" style={{ flex: 1, padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div className="row" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: 4, marginBottom: 30 }}>
            {[['login', t('auth.loginTab')], ['signup', t('auth.signupTab')]].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)} style={{
                flex: 1, height: 42, border: 'none', cursor: 'pointer', borderRadius: 'var(--r-sm)',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.05em',
                color: mode === k ? '#fff' : 'var(--fg-muted)', transition: 'all .25s var(--ease-out)',
                background: mode === k ? 'linear-gradient(120deg, var(--accent), var(--accent-deep))' : 'transparent',
                boxShadow: mode === k ? '0 6px 18px -8px var(--accent-glow)' : 'none',
              }}>{l}</button>
            ))}
          </div>

          <h1 className="display" style={{ fontSize: 30, margin: '0 0 6px' }}>
            {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
          </h1>
          <p style={{ color: 'var(--fg-muted)', margin: '0 0 26px', fontSize: 14 }}>
            {mode === 'login' ? 'Войди, чтобы продолжить регистрацию на турнир.' : 'Пара полей — и можно подавать заявку.'}
          </p>

          <div className="col gap-4" key={mode} style={{ animation: 'fadeUp 0.4s var(--ease-out) both' }}>
            {mode === 'signup' && (
              <div className="row gap-3 field-row-2col" style={{ alignItems: 'flex-start' }}>
                <Field label="Имя" required error={err.name}>
                  <Input placeholder="Алихан" value={f.name} error={err.name} onChange={e => set('name', e.target.value)} />
                </Field>
                <Field label="Ник" required error={err.nick}>
                  <Input placeholder="s1lent" value={f.nick} error={err.nick} onChange={e => set('nick', e.target.value)} />
                </Field>
              </div>
            )}
            <Field label={t('auth.email')} required error={err.email} icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>}>
              <Input type="email" placeholder="you@mail.kz" value={f.email} error={err.email} icon onChange={e => set('email', e.target.value)} />
            </Field>
            {mode === 'signup' && (
              <Field label="Телефон" required error={err.phone} valid={isValidKzPhone(f.phone)} hint="Казахстанский номер: +7 (7XX) XXX-XX-XX">
                <PhoneInput value={f.phone} error={err.phone} onChange={v => set('phone', v)} />
              </Field>
            )}
            <Field label={t('auth.password')} required error={err.pass} icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>}>
              <Input type="password" placeholder="••••••••" value={f.pass} error={err.pass} icon onChange={e => set('pass', e.target.value)} />
            </Field>

            {mode === 'login' && (
              <div className="row" style={{ justifyContent: 'flex-end' }}>
                <button onClick={() => {}} style={{ fontSize: 13, color: 'var(--fg-muted)', cursor: 'pointer', background: 'none', border: 'none' }}>{t('auth.forgotPassword')}</button>
              </div>
            )}

            <Btn size="lg" full onClick={submit} disabled={busy} icon={busy ? <Spinner size={18} /> : null}>
              {busy ? t('common.loading') : mode === 'login' ? t('auth.loginBtn') : t('auth.createBtn')}
            </Btn>

            <OAuthButtons dividerLabel={t('common.or')} />
          </div>

          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: 'var(--fg-muted)' }}>
            {mode === 'login' ? 'Ещё нет аккаунта? ' : 'Уже есть аккаунт? '}
            <button onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} style={{ color: 'var(--accent-bright)', cursor: 'pointer', fontWeight: 600, background: 'none', border: 'none', fontSize: 13 }}>
              {mode === 'login' ? t('auth.signupTab') : t('auth.loginTab')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
